import { api } from './api';
import type {
  CategoryNode,
  DatasetSummary,
  DatasetField,
  DatasetFile,
  DatasetMethodology,
} from '../types/api';

export interface AdminProfile {
  id: number;
  username: string;
  displayName: string | null;
  role: 'super_admin' | 'content_admin' | 'user_admin' | 'readonly';
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
}

export interface ImportTask {
  id: number;
  taskType: string;
  sourceFilename: string | null;
  uploadedByAdminId: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalRows: number | null;
  createdRows: number | null;
  updatedRows: number | null;
  errorRows: number | null;
  summary: Record<string, unknown> | null;
  errorMessage: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface ProcessingJob {
  id: number;
  jobType: string;
  datasetId: number | null;
  payload: Record<string, unknown> | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  result: Record<string, unknown> | null;
  errorMessage: string | null;
  elapsedSec: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface DashboardDailyPoint {
  date: string;
  views: number;
  distinctDatasets: number;
  taobaoRedirects: number;
  redemptions: number;
}
export interface DashboardDaily {
  days: number;
  series: DashboardDailyPoint[];
  summary: {
    visitorsToday: number;
    onlineNow: number;
    totalViews: number;
    totalRedirects: number;
    totalRedemptions: number;
    avgViewsPerDay: number;
    avgRedirectsPerDay: number;
    avgRedemptionsPerDay: number;
    today: { views: number; distinctDatasets: number; taobaoRedirects: number; redemptions: number };
  };
}

export interface OnlineVisitor {
  email: string | null;
  ip: string | null;
  lastSeenAt: string;
  isAnonymous: boolean;
}
export interface OnlineVisitors {
  count: number;
  visitors: OnlineVisitor[];
}

export type TrendBucket = 'day' | 'week' | 'month';
export interface TrendPoint {
  key: string;
  views: number;
  taobaoRedirects: number;
  redemptions: number;
}
export interface DashboardTrend {
  bucket: TrendBucket;
  series: TrendPoint[];
}
export interface IntradayPoint {
  hour: string;
  views: number;
  taobaoRedirects: number;
  redemptions: number;
}
export interface DashboardIntraday {
  date: string;
  series: IntradayPoint[];
}

export const adminApi = {
  login: (username: string, password: string) =>
    api.post<{ admin: AdminProfile; accessToken: string }>('/admin/auth/login', { username, password }),
  refresh: () => api.post<{ admin: AdminProfile; accessToken: string }>('/admin/auth/refresh'),
  logout: () => api.post('/admin/auth/logout'),
  me: () => api.get<{ admin: AdminProfile }>('/admin/auth/me'),

  // 仪表盘日维度统计（浏览 / 转淘宝 / 兑换）
  dashboardDaily: (days = 30) =>
    api.get<DashboardDaily>('/admin/dashboard/daily', { params: { days } }).then((r) => r.data),

  // 当前在线访客明细（近 5 分钟）
  dashboardOnline: () =>
    api.get<OnlineVisitors>('/admin/dashboard/online').then((r) => r.data),

  // 趋势序列（每日 / 每周 / 每月）
  dashboardTrend: (bucket: TrendBucket = 'day') =>
    api.get<DashboardTrend>('/admin/dashboard/trend', { params: { bucket } }).then((r) => r.data),

  // 日内 24 小时序列（某中国本地日）
  dashboardIntraday: (date: string) =>
    api.get<DashboardIntraday>('/admin/dashboard/intraday', { params: { date } }).then((r) => r.data),

  // 有数据的日期清单（日内趋势日期选择器）
  dashboardAvailableDates: () =>
    api.get<{ dates: string[] }>('/admin/dashboard/available-dates').then((r) => r.data.dates),

  // categories
  listCategories: () => api.get<{ items: CategoryNode[] }>('/admin/categories'),
  categoriesTree: () => api.get<{ tree: CategoryNode[] }>('/admin/categories/tree'),
  toggleCategoryEnabled: (code: string, enabled: boolean) =>
    api.patch<{ item: CategoryNode }>(`/admin/categories/${encodeURIComponent(code)}/enabled`, { enabled }),
  uploadHierarchy: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ importTaskId: number; status: string }>('/admin/categories/import', fd);
  },
  getImportTask: (id: number) =>
    api.get<{ task: ImportTask }>(`/admin/categories/import-tasks/${id}`),

  // datasets
  listDatasets: (params: { status?: string; search?: string; page?: number; pageSize?: number }) =>
    api.get<{ items: DatasetSummary[]; total: number; page: number; pageSize: number }>(
      '/admin/datasets',
      { params },
    ),
  datasetDetail: (code: string) =>
    api.get<{
      dataset: DatasetSummary;
      files: DatasetFile[];
      fields: DatasetField[];
      methodologies: DatasetMethodology[];
    }>(`/admin/datasets/${encodeURIComponent(code)}`),
  setDatasetStatus: (code: string, status: string) =>
    api.patch<{ dataset: DatasetSummary }>(`/admin/datasets/${encodeURIComponent(code)}/status`, { status }),
  importDatasetCsv: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ importTaskId: number; status: string }>('/admin/datasets/import', fd);
  },
  uploadDatasetZip: (code: string, file: File, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ jobId: number; datasetId: number; status: string }>(
      `/admin/datasets/${encodeURIComponent(code)}/upload`,
      fd,
      {
        onUploadProgress: (e) => {
          if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
  },
  batchUploadDatasets: (file: File, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ importTaskId: number; status: string }>(
      '/admin/datasets/batch-upload',
      fd,
      {
        // 不设 axios timeout：批量上传可能耗时数十分钟到数小时
        timeout: 0,
        onUploadProgress: (e) => {
          if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
  },
  megaUpload: (file: File, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ importTaskId: number; status: string }>(
      '/admin/datasets/mega-upload',
      fd,
      {
        // 不设 axios timeout：mega-zip 可能很大、ingest 耗时数十分钟到数小时
        timeout: 0,
        onUploadProgress: (e) => {
          if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
  },
  rerunMegaFromRaw: (rawFilename: string) =>
    api.post<{ importTaskId: number; status: string }>(
      '/admin/datasets/mega-upload/rerun-from-raw',
      { rawFilename },
    ),
  uploadFieldDict: (file: File, clearMissing: boolean, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('clearMissing', clearMissing ? 'true' : 'false');
    return api.post<{ importTaskId: number; status: string }>(
      '/admin/datasets/field-dict/upload',
      fd,
      {
        onUploadProgress: (e) => {
          if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
  },
  uploadMethodologyBatch: (file: File, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ importTaskId: number; status: string }>(
      '/admin/datasets/methodology/upload-batch',
      fd,
      {
        timeout: 0,
        onUploadProgress: (e) => {
          if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
  },
  uploadDerivedProgress: (file: File, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ importTaskId: number; status: string }>(
      '/admin/datasets/derived-progress/upload',
      fd,
      {
        onUploadProgress: (e) => {
          if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
  },
  listBatchRawFiles: () =>
    api.get<{ files: Array<{ name: string; size: number; modifiedAt: string }> }>(
      '/admin/datasets/batch-raw-files',
    ),
  rerunBatchFromRaw: (rawFilename: string) =>
    api.post<{ importTaskId: number; status: string }>(
      '/admin/datasets/batch-upload/rerun-from-raw',
      { rawFilename },
    ),
  listRecentImportTasks: (limit = 20) =>
    api.get<{ tasks: ImportTask[] }>('/admin/datasets/import-tasks', { params: { limit } }),
  getDatasetImportTask: (id: number) =>
    api.get<{ task: ImportTask }>(`/admin/datasets/import-tasks/${id}`),
  getJob: (id: number) => api.get<{ job: ProcessingJob }>(`/admin/datasets/jobs/${id}`),

  // links（淘宝链接管理）
  listDatasetLinks: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<{
      items: Array<{ id: number; code: string; name: string; entityLevel: string | null; status: string; taobaoLink: string | null }>;
      total: number;
      page: number;
      pageSize: number;
    }>('/admin/links/datasets', { params }),
  listGroupLinks: () =>
    api.get<{ items: Array<{ id: number; code: string; name: string; level: number; taobaoLink: string | null }> }>(
      '/admin/links/groups',
    ),
  updateDatasetLink: (code: string, taobaoLink: string | null) =>
    api.patch<{ code: string; taobaoLink: string | null }>(
      `/admin/links/dataset/${encodeURIComponent(code)}`,
      { taobaoLink },
    ),
  updateGroupLink: (id: number, taobaoLink: string | null) =>
    api.patch<{ id: number; taobaoLink: string | null }>(`/admin/links/group/${id}`, { taobaoLink }),
  importDatasetLinks: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{
      total: number;
      updated: number;
      skipped: number;
      failed: number;
      errors: Array<{ line: number; code: string; reason: string }>;
    }>('/admin/links/import', fd);
  },
};
